import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react'

import withConfig from '../../../wrappers/withConfig'
import { filterModes } from '../box-plot/FilterModeSelector'
import { AppStateContext } from '../AppStateContext'
import { generateDatePostParams } from '../../../../utils/chart/values'
import { toast } from 'react-toastify'
import { ParameterContext } from '../../../wrappers/ParameterContext'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'
import { getColorByFacilityType } from '../../../../utils/chart/palette'

const DataContext = createContext(null)

export default withConfig(({ config, children }) => {
    const { API_URL } = config
    const { authenticatedFetch } = useContext(APIRequestContext)
    const {
        mapState: { selectedFeatures },
    } = useContext(AppStateContext)
    const { params } = useContext(ParameterContext)
    const [isCollapsed, setCollapsed] = useState(false)
    const [piperData, setPiperData] = useState([])
    const [loading, setLoading] = useState(false)
    const [containerDimensions, setContainerDimensions] = useState({
        width: 600,
        height: 600,
    })

    const [filterMode, setFilterMode] = useState(filterModes[0]) //Selected Wells, All Wells

    const [displayFilterModeDropdown, setDisplayFilterModeDropdown] = useState(
        false
    )
    const chartRef = useRef(null)
    const saveAsImageRef = useRef(null)

    const includeAllData = useMemo(() => filterMode === 'All Wells', [
        filterMode,
    ])

    const fetchChartData = useCallback(
        (allData, selectedFeatures) => {
            setLoading(true)
            const dates = generateDatePostParams(params)

            const ids = selectedFeatures.map(x => x.get('ClearinghouseNumber'))
            const body = JSON.stringify({
                ClearinghouseNums: ids,
                allData,
                dates,
            })
            authenticatedFetch(`${API_URL}/piper`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                body,
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(response => {
                    setPiperData(response.chartData)
                })
                .catch(e => {
                    toast({
                        level: 'error',
                        message:
                            'Box Plot: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                })
                .finally(() => setLoading(false))
        },
        [authenticatedFetch, setPiperData, setLoading, params]
    )

    //only call fetchChartData if includeAllData is all Wells
    useEffect(() => {
        // if include all data is true then only
        if (includeAllData) {
            setPiperData([])
            fetchChartData(true, [])
        }
    }, [fetchChartData, includeAllData])

    //call fetchChartData when selected features change
    useEffect(() => {
        if (
            filterMode === 'Selected Wells' &&
            selectedFeatures &&
            selectedFeatures.length
        ) {
            setPiperData([])
            fetchChartData(false, selectedFeatures)
        }
    }, [fetchChartData, selectedFeatures, filterMode])

    const piperLegendData = useMemo(
        () =>
            piperData.map(x => ({
                FacilityTypeDisplay: x.FacilityTypeDisplay,
                color: getColorByFacilityType(x.FacilityType),
            })),
        [piperData]
    )

    return (
        <DataContext.Provider
            value={{
                isCollapsed,
                setCollapsed,
                piperData,
                piperLegendData,
                loading,
                filterMode,
                setFilterMode,
                displayFilterModeDropdown,
                setDisplayFilterModeDropdown,
                containerDimensions,
                setContainerDimensions,
                chartRef,
                saveAsImageRef,
            }}
        >
            {children}
        </DataContext.Provider>
    )
})

export { DataContext }
